import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {getCourierDashboard} from "./courier-api";

export default function useCourierDashboard(tenantId, {courierId, status, page, size, courierPage, courierSize}) {
    const {i18n} = useTranslation();
    const language = i18n.language;
    const scope = `${tenantId}:${language}:${courierId}:${status}:${page}:${size}:${courierPage}:${courierSize}`;
    const [result, setResult] = useState({scope: null, loading: true, data: null, error: ""});
    const [retry, setRetry] = useState(0);

    useEffect(() => {
        const controller = new AbortController();
        let pending = false;
        const load = async () => {
            if (pending || document.visibilityState === "hidden") return;
            pending = true;
            setResult(previous => ({scope, loading: true, error: previous.scope === scope ? previous.error : "",
                data: previous.scope === scope ? previous.data : null}));
            try {
                const data = await getCourierDashboard({courierId: courierId || undefined, status: status || undefined,
                    page, size, courierPage, courierSize}, language, controller.signal);
                if (controller.signal.aborted) return;
                const valid = data.tenantId === tenantId && data.couriers.content.every(c => c.tenantId === tenantId);
                setResult({scope, loading: false, data: valid ? data : null, error: valid ? "" : "CourierDashboardLoadFailed"});
            } catch (error) {
                if (!controller.signal.aborted) setResult(previous => ({scope, loading: false,
                    // Keep verified data on temporary outages; permission/filter failures must clear it.
                    data: previous.scope === scope && (!error.response || error.response.status >= 500) ? previous.data : null,
                    error: error.response?.data?.message || "CourierDashboardLoadFailed"}));
            } finally { pending = false; }
        };
        load();
        const interval = setInterval(load, 15000);
        document.addEventListener("visibilitychange", load);
        window.addEventListener("focus", load);
        return () => {controller.abort(); clearInterval(interval);
            document.removeEventListener("visibilitychange", load); window.removeEventListener("focus", load);};
    }, [tenantId, courierId, status, page, size, courierPage, courierSize, language, scope, retry]);

    // Filter/account changes immediately hide the preceding scope, even before the next effect has run.
    return {...(result.scope === scope ? result : {loading: true, data: null, error: ""}), refresh: () => setRetry(value => value + 1)};
}
